import { addEntity, addComponent } from 'bitecs';
import { Time } from '../world.js';
import { SPAWN_TIME, TIME_SCALE, CURRENT_MODE } from '../config.js';

// Time constants - 1 real second = 1 game second
export const TICKS_PER_SECOND = 20;
export const TICKS_PER_MINUTE = TICKS_PER_SECOND * 60;
export const TICKS_PER_HOUR = TICKS_PER_MINUTE * 60;
export const TICKS_PER_DAY = TICKS_PER_HOUR * 24;

// Calendar constants
export const DAYS_PER_MONTH = 30;
export const MONTHS_PER_YEAR = 12;
export const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

// Time of day constants
export const DAY_START = 6 * TICKS_PER_HOUR;  // 6 AM
export const DAY_END = 18 * TICKS_PER_HOUR;   // 6 PM
export const NIGHT_START = 20 * TICKS_PER_HOUR; // 8 PM
export const NIGHT_END = 4 * TICKS_PER_HOUR;    // 4 AM
export const DAWN_DURATION = TICKS_PER_HOUR;    // 1 hour
export const DUSK_DURATION = TICKS_PER_HOUR;    // 1 hour

// Lighting constants
export const NIGHT_BRIGHTNESS = 0.1;
export const DAY_BRIGHTNESS = 1.0;

// Track last update time for delta calculation
let lastUpdateTime = 0;

// Cache time calculations
let lastTick = -1;
let cachedHour = 0;
let cachedMinute = 0;

// Smooth easing function for transitions
function smoothStep(x) {
    return x * x * (3 - 2 * x);
}

function getTimeInfo(tick) {
    if (tick === lastTick) {
        return { hour: cachedHour, minute: cachedMinute };
    }
    
    cachedHour = Math.floor(tick / TICKS_PER_HOUR);
    cachedMinute = Math.floor((tick % TICKS_PER_HOUR) / TICKS_PER_MINUTE);
    lastTick = tick;
    
    return { hour: cachedHour, minute: cachedMinute };
}

function getSeason(month) {
    // Spring: March-May (3-5)
    // Summer: June-August (6-8)
    // Autumn: September-November (9-11)
    // Winter: December-February (12,1,2)
    if (month >= 3 && month <= 5) return 0; // Spring
    if (month >= 6 && month <= 8) return 1; // Summer
    if (month >= 9 && month <= 11) return 2; // Autumn
    return 3; // Winter
}

export function createTimeSystem(world) {
    // Only create time entity if time is enabled in game mode
    if (!CURRENT_MODE.time) {
        return null;
    }

    // Create time entity
    const timeEntity = addEntity(world);
    addComponent(world, Time, timeEntity);
    
    // Initialize time at SPAWN_TIME (in hours)
    Time.ticks[timeEntity] = SPAWN_TIME * TICKS_PER_HOUR;
    Time.day[timeEntity] = 1;
    Time.month[timeEntity] = 1;
    Time.year[timeEntity] = 1;
    Time.season[timeEntity] = 0; // Start in Spring
    Time.isDay[timeEntity] = 0;  // Start at night
    Time.brightness[timeEntity] = NIGHT_BRIGHTNESS; // Start with moonlight
    
    // Initialize last update time
    lastUpdateTime = performance.now();
    
    return timeEntity;
}

export function timeSystem(world) {
    const timeEntity = world.timeEntity;
    if (!timeEntity || !CURRENT_MODE.time) return world;
    
    // Calculate time delta
    const currentTime = performance.now();
    const deltaTime = (currentTime - lastUpdateTime) / 1000; // Convert to seconds
    lastUpdateTime = currentTime;
    
    // Calculate ticks to advance based on real time and time scale
    const ticksToAdvance = Math.floor(deltaTime * TICKS_PER_SECOND * TIME_SCALE);
    
    // Only advance if we have ticks to process
    if (ticksToAdvance > 0) {
        // Update ticks
        Time.ticks[timeEntity] = (Time.ticks[timeEntity] + ticksToAdvance) % TICKS_PER_DAY;
        
        // Get cached time info
        const timeInfo = getTimeInfo(Time.ticks[timeEntity]);
        
        // Log time changes every minute
        if (Time.ticks[timeEntity] % TICKS_PER_MINUTE === 0) {
            console.log(`Game time: ${timeInfo.hour}:${timeInfo.minute.toString().padStart(2, '0')}`);
        }
        
        // Check for new day
        if (Time.ticks[timeEntity] === 0) {
            Time.day[timeEntity]++;
            console.log('New day:', Time.day[timeEntity]);
            
            // Check for new month
            if (Time.day[timeEntity] > DAYS_PER_MONTH) {
                Time.day[timeEntity] = 1;
                Time.month[timeEntity]++;
                console.log('New month:', Time.month[timeEntity]);
                
                // Check for new year
                if (Time.month[timeEntity] > MONTHS_PER_YEAR) {
                    Time.month[timeEntity] = 1;
                    Time.year[timeEntity]++;
                    console.log('New year:', Time.year[timeEntity]);
                }
                
                // Update season
                Time.season[timeEntity] = getSeason(Time.month[timeEntity]);
                console.log('New season:', Time.season[timeEntity]);
            }
        }
        
        // Update day/night state
        const isDay = Time.ticks[timeEntity] >= DAY_START && Time.ticks[timeEntity] < DAY_END;
        Time.isDay[timeEntity] = isDay ? 1 : 0;
        
        // Calculate brightness based on time of day with smooth transitions
        let brightness = DAY_BRIGHTNESS;
        
        if (Time.ticks[timeEntity] >= DAY_END && Time.ticks[timeEntity] < NIGHT_START) {
            // Sunset transition
            const progress = (Time.ticks[timeEntity] - DAY_END) / DUSK_DURATION;
            brightness = DAY_BRIGHTNESS - (DAY_BRIGHTNESS - NIGHT_BRIGHTNESS) * smoothStep(progress);
        } else if (Time.ticks[timeEntity] >= NIGHT_START || Time.ticks[timeEntity] < NIGHT_END) {
            // Night
            brightness = NIGHT_BRIGHTNESS;
        } else if (Time.ticks[timeEntity] >= NIGHT_END && Time.ticks[timeEntity] < DAY_START) {
            // Sunrise transition
            const progress = (Time.ticks[timeEntity] - NIGHT_END) / DAWN_DURATION;
            brightness = NIGHT_BRIGHTNESS + (DAY_BRIGHTNESS - NIGHT_BRIGHTNESS) * smoothStep(progress);
        }
        
        Time.brightness[timeEntity] = brightness;
    }
    
    return world;
} 